import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { IResource, ISerializer } from '@koomzo/coremodel';
import { WhereDbQueryFilterConfig } from '@koomzo/commonmodel';
import { Observable, Subscription, map } from 'rxjs';

import { ApplicationDbService } from '@shared/services/application-db.service';
import { BaseDbServicesWrapper } from '@app/shared/services/db/base-db-services-wrapper';

import { INVENTORY_SUMMARY_COLLECTION, InventorySummaryDoc, SummaryCounts, summaryCounts } from '../rules/stock-summary.rules';

export type IInventorySummary = InventorySummaryDoc & IResource;

class InventorySummarySerializer implements ISerializer {
  fromJson(json: IInventorySummary): IInventorySummary {
    return { ...json };
  }
  toJson(model: IInventorySummary): IInventorySummary {
    return { ...model };
  }
}

/** `inventory-summary/{tenantId}` — read only: the stock-summary triggers write it (`firestore.rules`: `write: if false`). */
@Injectable({ providedIn: 'root' })
export class InventorySummaryDbService extends ApplicationDbService<IInventorySummary> {
  constructor(baseDbServicesWrapper: BaseDbServicesWrapper<IInventorySummary>) {
    super(baseDbServicesWrapper, new InventorySummarySerializer(), INVENTORY_SUMMARY_COLLECTION);
  }

  /** The tenant's summary, live: `null` while there is none (a `where tenantId ==` query, as the rules read it). */
  watch(tenantId: string): Observable<IInventorySummary | null> {
    const query = new WhereDbQueryFilterConfig({ operatorField: 'tenantId', value: tenantId });
    return this.getListener(query).pipe(map((docs) => docs?.find((d) => d.id === tenantId) ?? docs?.[0] ?? null));
  }
}

/**
 * The Inventory summary, followed (kz2 M12 leftovers): ONE document per business, kept by the server, that carries
 * what the Inventory badges count — so the rail and the launcher tile show them from the start of the session, before
 * the person opens Inventory and its eight streams start. `inventory-attention.ts` blends it: the summary until the
 * live stock has loaded, the live figures after.
 *
 * Under `-c=mock` there are no triggers, so there is no document: `answered` turns true with nothing, and the badges
 * read as they did before (absent until Inventory is opened). "No document" is never read as "nothing open": the
 * open work stays unknown until the live stock has loaded (`inventory-attention.ts`).
 */
@Injectable({ providedIn: 'root' })
export class InventorySummaryService {
  private readonly db = inject(InventorySummaryDbService);
  private readonly _doc = signal<IInventorySummary | null>(null);
  private readonly _answered = signal(false);
  private sub: Subscription | null = null;
  private tenantId: string | null = null;

  /** The document as last read; `null` before the first answer, or when the business has none. */
  readonly doc: Signal<IInventorySummary | null> = this._doc.asReadonly();
  /** The listener has answered (with a document or without) for the tenant it follows; never after an error. */
  readonly answered: Signal<boolean> = this._answered.asReadonly();
  /** The badges' figures from it — zeros without a document. */
  readonly counts: Signal<SummaryCounts> = computed(() => summaryCounts(this._doc()));

  /** Follow one business's summary (once per tenant; another tenant replaces it). */
  watch(tenantId: string): void {
    if (this.sub && this.tenantId === tenantId) {
      return;
    }
    this.stop();
    this.tenantId = tenantId;
    this.sub = this.db.watch(tenantId).subscribe({
      next: (doc) => {
        this._doc.set(doc);
        this._answered.set(true);
      },
      /* Never fatal: the badges wait for the live stock instead, as they did before the summary. An error is no
         answer — nothing may be concluded from it (a winding capability must not retire on it). */
      error: (err) => {
        console.warn('[inventory] summary unavailable', err);
        this._doc.set(null);
        this._answered.set(false);
      },
    });
  }

  /** Stop following (sign-out, Inventory switched off). */
  stop(): void {
    this.sub?.unsubscribe();
    this.sub = null;
    this.tenantId = null;
    this._doc.set(null);
    this._answered.set(false);
  }
}
